import React, { useEffect, useState } from 'react';
import { Button, Modal, Table, Form, Input, InputNumber, Select, Popconfirm, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Breakpoint } from 'antd/es/_util/responsiveObserver';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import { paymentAPI, projectAPI, staffAPI } from '../services/api';
import { formatCurrencySimple } from '../utils/currency';
import { DEFAULT_CURRENCY_CODE } from '../constants/localeOptions';
import StudioNavBar from '../components/common/StudioNavBar';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Payments: React.FC = () => {
  const { user, isStaff } = useAuth();
  const [payments, setPayments] = useState<any[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [producers, setProducers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [viewingPayment, setViewingPayment] = useState<any | null>(null);
  const [editingPayment, setEditingPayment] = useState<any | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [form] = Form.useForm();
  const selectedProjectId = Form.useWatch('project', form);

  const selectedProject = projects.find((project: any) => Number(project.id) === Number(selectedProjectId));
  const selectedProjectCurrency = selectedProject?.currency_code || editingPayment?.currency_code || DEFAULT_CURRENCY_CODE;

  const getRemainingBalanceForProject = (project: any, currentPayment?: any) => {
    if (!project) return 0;
    const agreedPrice = Number(project.agreed_price || 0);
    const totalPaid = Number(project.total_paid || 0);
    const currentAmount = currentPayment && Number(currentPayment.project) === Number(project.id)
      ? Number(currentPayment.amount || 0)
      : 0;
    const remaining = agreedPrice - (totalPaid - currentAmount);
    return Number.isFinite(remaining) ? Math.max(remaining, 0) : 0;
  };

  const selectedProjectRemainingBalance = getRemainingBalanceForProject(selectedProject, editingPayment);

  const canEditPayment = (payment: any) => {
    return true; // All roles can handle payments now
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleView = (payment: any) => {
    setViewingPayment(payment);
  };

  const handleEdit = (payment: any) => {
    if (isStaff && !canEditPayment(payment)) {
      message.error('You can only edit payments you received');
      return;
    }
    setEditingPayment(payment);
    const formValues = {
      ...payment,
      project: payment.project ? Number(payment.project) : undefined,
      received_by: payment.received_by ? Number(payment.received_by) : undefined,
    };
    if (isStaff) {
      const project = projects.find((item: any) => Number(item.id) === Number(payment.project));
      formValues.amount = getRemainingBalanceForProject(project, payment);
    }
    setTimeout(() => {
      form.setFieldsValue(formValues);
    }, 0);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await paymentAPI.deletePayment(id);
      message.success('Payment deleted successfully');
      fetchPayments();
      fetchProjects();
      fetchPaymentHistory();
    } catch (err) {
      message.error('Failed to delete payment');
    }
  };

  const handleDeleteHistory = async (id: number) => {
    try {
      await paymentAPI.deletePaymentHistory(id);
      message.success('Payment history deleted successfully');
      fetchPaymentHistory();
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      message.error(typeof detail === 'string' ? detail : 'Failed to delete payment history');
    }
  };

  const responsiveSm: Breakpoint[] = ['sm'];
  const responsiveMd: Breakpoint[] = ['md'];
  const responsiveLg: Breakpoint[] = ['lg'];

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'custom_id',
      key: 'custom_id',
      sorter: true,
      width: 100,
      render: (_: any, record: any) => record.custom_id || record.id
    },
    {
      title: 'Project',
      dataIndex: 'project',
      key: 'project',
      sorter: true,
      width: 160,
      render: (_: any, record: any) => record.project_custom_id || record.project,
      responsive: responsiveSm
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 150,
      render: (value: any, record: any) => formatCurrencySimple(value, record.currency_code || record.project_currency_code)
    },
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      sorter: true,
      width: 110,
      responsive: responsiveMd
    },
    {
      title: 'Method',
      dataIndex: 'payment_method',
      key: 'payment_method',
      sorter: true,
      width: 120,
      responsive: responsiveLg,
      render: (method: string) => method?.replace('_', ' ')
    },
    {
      title: 'Received By',
      dataIndex: 'received_by_name',
      key: 'received_by_name',
      sorter: true,
      width: 140,
      responsive: responsiveLg,
      ellipsis: true
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: any, record: any) => {
        const canEdit = canEditPayment(record);
        return (
          <div className="flex gap-1">
            {!canEdit ? (
              <Button icon={<EyeOutlined />} onClick={() => handleView(record)} size="small">
                View
              </Button>
            ) : (
              <>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  size="small"
                  className="hidden sm:inline-flex"
                >
                  Edit
                </Button>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                  size="small"
                  className="sm:hidden"
                />
                <Popconfirm
                  title="Delete payment?"
                  description="Are you sure you want to delete this payment?"
                  onConfirm={() => handleDelete(record.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
              </>
            )}
          </div>
        );
      }
    },
  ];

  const historyColumns: ColumnsType<any> = [
    {
      title: 'When',
      dataIndex: 'changed_at',
      key: 'changed_at',
      width: 180,
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (value: string) => value?.toUpperCase(),
    },
    {
      title: 'Project',
      key: 'project',
      width: 180,
      render: (_: any, record: any) => record.project_custom_id || record.project_name || record.project,
    },
    {
      title: 'Previous',
      dataIndex: 'previous_amount',
      key: 'previous_amount',
      width: 140,
      render: (value: any, record: any) => formatCurrencySimple(value, record.currency_code || DEFAULT_CURRENCY_CODE),
    },
    {
      title: 'New',
      dataIndex: 'new_amount',
      key: 'new_amount',
      width: 140,
      render: (value: any, record: any) => formatCurrencySimple(value, record.currency_code || DEFAULT_CURRENCY_CODE),
    },
    {
      title: 'Producer',
      dataIndex: 'received_by_name',
      key: 'received_by_name',
      width: 180,
      responsive: responsiveLg,
      render: (value: string) => value || 'N/A',
    },
    {
      title: 'Changed By',
      dataIndex: 'changed_by_name',
      key: 'changed_by_name',
      width: 180,
      responsive: responsiveLg,
      render: (value: string) => value || 'System',
    },
    {
      title: 'Receipt',
      key: 'receipt',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <div className="flex items-center gap-1">
          <Button
            type="link"
            size="small"
            icon={<FilePdfOutlined />}
            onClick={() => downloadReceiptPdf(record)}
          >
            Download
          </Button>
          {!isStaff && (
            <Popconfirm
              title="Delete history record?"
              description="This removes only the history entry, not the payment."
              onConfirm={() => handleDeleteHistory(record.id)}
              okText="Delete"
              cancelText="Cancel"
            >
              <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const buildPdfHeader = (doc: jsPDF, title: string, subtitle: string) => {
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, 210, 34, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.text('SOMS Studio', 14, 14);
    doc.setFontSize(11);
    doc.text(title, 14, 22);
    doc.setTextColor(226, 232, 240);
    doc.setFontSize(9);
    doc.text(subtitle, 14, 28);
    doc.setTextColor(15, 23, 42);
  };

  const downloadAllHistoryPdf = () => {
    if (!paymentHistory.length) {
      message.warning('No payment history records available to export');
      return;
    }

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const generatedAt = new Date();
    const studioLabel = user?.studio_name || user?.username || 'SOMS Studio';
    buildPdfHeader(
      doc,
      'Payment History Report',
      `Prepared for ${studioLabel} • ${generatedAt.toLocaleString()}`
    );

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(14, 40, 182, 20, 2, 2, 'F');
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    doc.text(`Total records: ${paymentHistory.length}`, 18, 48);
    doc.text(`Generated by: ${user?.username || 'System'}`, 18, 54);

    autoTable(doc, {
      startY: 66,
      head: [['Date/Time', 'Action', 'Project', 'Previous', 'New', 'Method', 'Producer', 'Changed By']],
      body: paymentHistory.map((entry: any) => [
        new Date(entry.changed_at).toLocaleString(),
        (entry.action || '').toUpperCase(),
        entry.project_custom_id || entry.project_name || 'Project',
        formatCurrencySimple(entry.previous_amount, entry.currency_code || DEFAULT_CURRENCY_CODE),
        formatCurrencySimple(entry.new_amount, entry.currency_code || DEFAULT_CURRENCY_CODE),
        (entry.payment_method || '').replace('_', ' '),
        entry.received_by_name || 'N/A',
        entry.changed_by_name || 'System',
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      theme: 'striped',
      margin: { left: 14, right: 14 },
    });

    const fileDate = generatedAt.toISOString().slice(0, 10);
    doc.save(`payment-history-report-${fileDate}.pdf`);
    message.success('Payment history PDF downloaded');
  };

  const downloadReceiptPdf = (record: any) => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const studioLabel = user?.studio_name || user?.username || 'SOMS Studio';
    const receiptRef = `RCPT-${record.id}`;

    buildPdfHeader(
      doc,
      'Payment Update Receipt',
      `Receipt ${receiptRef} • ${new Date(record.changed_at).toLocaleString()}`
    );

    doc.setFillColor(248, 250, 252);
    doc.roundedRect(14, 40, 182, 12, 2, 2, 'F');
    doc.setFontSize(10);
    doc.setTextColor(51, 65, 85);
    doc.text(`Studio: ${studioLabel}`, 18, 47);

    autoTable(doc, {
      startY: 58,
      head: [['Field', 'Value']],
      body: [
        ['Receipt Reference', receiptRef],
        ['Project', `${record.project_custom_id || ''} ${record.project_name || ''}`.trim() || 'Project'],
        ['Action', (record.action || '').toUpperCase()],
        ['Payment Method', (record.payment_method || '').replace('_', ' ')],
        ['Previous Amount', formatCurrencySimple(record.previous_amount, record.currency_code || DEFAULT_CURRENCY_CODE)],
        ['New Amount', formatCurrencySimple(record.new_amount, record.currency_code || DEFAULT_CURRENCY_CODE)],
        ['Producer', record.received_by_name || 'N/A'],
        ['Changed By', record.changed_by_name || 'System'],
        ['Change Date/Time', new Date(record.changed_at).toLocaleString()],
      ],
      styles: {
        fontSize: 10,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 55, fontStyle: 'bold', fillColor: [241, 245, 249] },
        1: { cellWidth: 120 },
      },
      headStyles: {
        fillColor: [30, 41, 59],
        textColor: [255, 255, 255],
      },
      margin: { left: 14, right: 14 },
      theme: 'grid',
    });

    const endY = (doc as any).lastAutoTable?.finalY || 120;
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(9);
    doc.text('This is a system-generated receipt from SOMS Studio Operations Management System.', 14, endY + 12);
    doc.text('Keep this receipt for accounting and reconciliation records.', 14, endY + 18);

    doc.save(`payment-receipt-${receiptRef}.pdf`);
    message.success('Receipt downloaded');
  };

  useEffect(() => {
    fetchPayments();
    fetchProjects();
    fetchProducers();
    fetchPaymentHistory();
  }, []);

  useEffect(() => {
    if (!showModal || !selectedProject) return;
    if (!isStaff) return;

    const currentAmount = form.getFieldValue('amount');
    const shouldAutofill = currentAmount === undefined || currentAmount === null || !editingPayment;
    if (shouldAutofill) {
      form.setFieldValue('amount', selectedProjectRemainingBalance);
    }
  }, [showModal, selectedProjectId, selectedProject, isStaff, editingPayment, selectedProjectRemainingBalance, form]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await paymentAPI.getPayments();
      setPayments(res.data || []);
    } catch (err) {
      message.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await projectAPI.getProjects();
      setProjects(res.data || []);
    } catch (err) {
      message.error('Failed to fetch projects');
    }
  };

  const fetchProducers = async () => {
    try {
      const res = await staffAPI.getStaff();
      setProducers(res.data || []);
    } catch (err) {
      message.error('Failed to fetch producers');
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const res = await paymentAPI.getPaymentHistory();
      setPaymentHistory(res.data || []);
    } catch (err) {
      message.error('Failed to fetch payment history');
    }
  };

  const handleAdd = () => {
    setEditingPayment(null);
    form.resetFields();
    if (user?.id) {
      form.setFieldsValue({ received_by: Number(user.id) });
    }
    setShowModal(true);
  };

  const handleSave = async (values: any) => {
    try {
      const project = projects.find((item: any) => Number(item.id) === Number(values.project));
      const maxAllowed = getRemainingBalanceForProject(project, editingPayment);

      if (Number(values.amount || 0) > maxAllowed) {
        message.error(`Amount cannot exceed the remaining balance (${formatCurrencySimple(maxAllowed, project?.currency_code || DEFAULT_CURRENCY_CODE)})`);
        return;
      }

      if (values.custom_id) {
        const duplicateExists = payments.some((payment: any) =>
          payment.custom_id === values.custom_id &&
          (!editingPayment || payment.id !== editingPayment.id)
        );

        if (duplicateExists) {
          message.error(`Payment with ID "${values.custom_id}" already exists in your studio`);
          return;
        }
      }

      const formattedValues = {
        custom_id: values.custom_id || null,
        project: values.project,
        amount: values.amount,
        payment_method: values.payment_method,
        received_by: values.received_by ? Number(values.received_by) : null,
      };

      if (editingPayment) {
        await paymentAPI.updatePayment(editingPayment.id, formattedValues);
      } else {
        await paymentAPI.createPayment(formattedValues);
      }
      setShowModal(false);
      form.resetFields();
      message.success('Payment saved successfully');
      fetchPayments();
      fetchProjects();
      fetchPaymentHistory();
    } catch (err: any) {
      console.error('Save error:', err);
      if (err.response?.data?.custom_id) {
        message.error(err.response.data.custom_id[0]);
      } else {
        message.error('Failed to save payment');
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <StudioNavBar />
      <main className="mx-auto max-w-7xl px-4 py-6 sm:py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-3 sm:mb-8 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Payments</h1>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">Record client payments and track producer receipts with ease.</p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-end">
            <Button icon={<DownloadOutlined />} onClick={downloadAllHistoryPdf} size="large" className="w-full sm:w-auto">
              Download History PDF
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} size="large" className="w-full sm:w-auto">
              Add Payment
            </Button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <Table
              columns={columns}
              dataSource={payments}
              loading={loading}
              rowKey="id"
              pagination={{ pageSize: 10, responsive: true }}
              scroll={{ x: 'max-content' }}
              size="middle"
            />
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
            <h2 className="text-lg font-semibold text-slate-900 sm:text-xl">Payment Update History</h2>
            <p className="mt-1 text-xs text-slate-500 sm:text-sm">
              {isStaff
                ? 'Only your payment history records are shown.'
                : 'All producer payment updates across your studio are shown.'}
            </p>
          </div>
          <div className="overflow-x-auto">
            <Table
              columns={historyColumns}
              dataSource={paymentHistory}
              rowKey="id"
              pagination={{ pageSize: 8, responsive: true }}
              scroll={{ x: 'max-content' }}
              size="middle"
            />
          </div>
        </div>
      </main>

      <Modal
        title={editingPayment ? 'Edit Payment' : 'Add Payment'}
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingPayment ? 'Update' : 'Create'}
        width={isMobile ? '95vw' : 600}
        style={isMobile ? { maxWidth: '95vw' } : undefined}
      >
        <Form form={form} layout="vertical" onFinish={handleSave} preserve={false}>
          <Form.Item name="custom_id" label="Payment ID">
            <Input placeholder="Enter payment ID" size="large" />
          </Form.Item>
          <Form.Item name="project" label="Project" rules={[{ required: true, message: 'Please select a project' }]}>
            <Select
              placeholder="Select project by ID"
              size="large"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={projects.map(project => ({
                value: project.id,
                label: `#${project.custom_id || project.id} - ${project.song_title} (${project.client_name}) [${project.currency_code || DEFAULT_CURRENCY_CODE}]`
              }))}
            />
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true, message: 'Please enter amount' }]}>
            <InputNumber
              style={{ width: '100%' }}
              size="large"
              addonBefore={
                <Select
                  value={selectedProjectCurrency}
                  disabled
                  options={[{ value: selectedProjectCurrency, label: selectedProjectCurrency }]}
                  style={{ width: 95 }}
                  popupMatchSelectWidth={false}
                />
              }
              min={0}
              max={selectedProjectRemainingBalance}
              precision={selectedProjectCurrency === 'USD' ? 2 : 0}
              placeholder="Enter amount"
            />
          </Form.Item>
          <p className="-mt-4 mb-4 text-xs text-gray-500">
            Payment currency automatically follows the selected project. Remaining balance: {formatCurrencySimple(selectedProjectRemainingBalance, selectedProjectCurrency)}.
          </p>
          <Form.Item name="payment_method" label="Payment Method" rules={[{ required: true, message: 'Please select payment method' }]}>
            <Select
              placeholder="Select payment method"
              size="large"
              options={[
                { value: 'cash', label: 'Cash' },
                { value: 'mobile_money', label: 'Mobile Money' },
                { value: 'bank_transfer', label: 'Bank Transfer' },
                { value: 'card', label: 'Card' },
                { value: 'other', label: 'Other' }
              ]}
            />
          </Form.Item>
          <Form.Item name="received_by" label="Received By">
            <Select
              placeholder="Select a producer"
              size="large"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              options={producers.map(producer => ({
                value: producer.id,
                label: `${producer.first_name} ${producer.last_name}`
              }))}
            />
          </Form.Item>
          {isStaff && (
            <p className="-mt-4 mb-4 text-xs text-gray-500">
              As a producer, payments are automatically assigned to you.
            </p>
          )}
        </Form>
      </Modal>

      <Modal
        title="Payment Details"
        open={!!viewingPayment}
        onCancel={() => setViewingPayment(null)}
        footer={[
          <Button key="close" onClick={() => setViewingPayment(null)}>
            Close
          </Button>
        ]}
        width={isMobile ? '95vw' : 500}
        style={isMobile ? { maxWidth: '95vw' } : undefined}
      >
        {viewingPayment && (
          <div className="space-y-3">
            <div><strong>Payment ID:</strong> {viewingPayment.custom_id || viewingPayment.id}</div>
            <div><strong>Project:</strong> {viewingPayment.project_custom_id || viewingPayment.project}</div>
            <div><strong>Amount:</strong> {formatCurrencySimple(viewingPayment.amount, viewingPayment.currency_code || viewingPayment.project_currency_code)}</div>
            <div><strong>Date:</strong> {viewingPayment.payment_date}</div>
            <div><strong>Payment Method:</strong> {viewingPayment.payment_method?.replace('_', ' ')}</div>
            <div><strong>Currency:</strong> {viewingPayment.currency_code || viewingPayment.project_currency_code || DEFAULT_CURRENCY_CODE}</div>
            <div><strong>Received By:</strong> {viewingPayment.received_by_name || 'N/A'}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Payments;
